import * as React from "react";
import { fixed } from "./displayValues";
import { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { DataGrid } from "@mui/x-data-grid";
import CustomPagination from "./CustomPagination";
import { useGetDSMMetersQuery, useGetDSMMeterFindPageQuery } from "./operatorDashboardAPISlice";
import { getToken, concatPathWithParams } from "../api/apiHelper";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import FlagIcon from "@mui/icons-material/Flag";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import config from "../../config";

function formatSAST(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const parts = new Intl.DateTimeFormat("en-ZA", {
      timeZone: "Africa/Johannesburg",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const p = Object.fromEntries(parts.map(({ type, value: v }) => [type, v]));
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} SAST`;
  } catch {
    return value;
  }
}

const DEVICE_CSV_HEADERS = [
  ["serial_number", "Serial Number", (v) => v],
  ["hwi", "Hot Water Index", (v) => { const n = parseFloat(v); return isNaN(n) ? "" : parseFloat(n.toFixed(2)).toString(); }],
  ["switch_status", "Switch Status", (v) => v ?? ""],
  ["control_status", "Control Status", (v) => v ?? ""],
  ["power", "Power (kW)", (v) => { const n = parseFloat(v); return isNaN(n) ? "" : fixed(n); }],
  ["latest_power_rating", "Max Power (kW)", (v) => { const n = parseFloat(v); return isNaN(n) ? "" : fixed(n); }],
  ["last_switch_controlled", "Last Switch Controlled (SAST)", (v) => formatSAST(v)],
  ["lower_limit_flagged", "Lower Limit (%)", (v) => (v ? "Yes" : "No")],
  ["is_baseline", "Baseline", (v) => (v ? "Yes" : "No")],
  ["forced_status", "Forced Status", (v) => v === "ForcedOn" ? "Force On" : v === "ForcedOff" ? "Force Off" : ""],
];

function downloadCSV(rows, headers, filename) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const headerRow = headers.map(([, label]) => label).join(",");
  const dataRows = rows.map((row) =>
    headers.map(([key, , transform]) => escape(transform ? transform(row[key], row) : row[key])).join(",")
  );
  const csv = "\uFEFF" + [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Filter / Sort config ─────────────────────────────────────────────────────

const FILTER_FIELDS = [
  { value: "hwi",                    label: "HWI",              type: "numeric" },
  { value: "power",                  label: "Power (kW)",       type: "numeric" },
  { value: "latest_power_rating",    label: "Max Power (kW)",   type: "numeric" },
  { value: "lower_limit_flagged",    label: "Lower Limit (%)",  type: "boolean" },
  { value: "last_switch_controlled", label: "Last Switch",      type: "date"    },
  { value: "is_baseline",            label: "Baseline",         type: "boolean" },
];

const NUMERIC_OPERATORS = [
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
  { value: "eq",  label: "=" },
];

const DATE_OPERATORS = [
  { value: "gte", label: "after"  },
  { value: "lte", label: "before" },
  { value: "eq",  label: "on"     },
];

const SORT_FIELDS = [
  { value: "serial_number",          label: "Serial" },
  { value: "last_switch_controlled", label: "Last Switch" },
];

const SORT_FIELD_LABELS = Object.fromEntries(SORT_FIELDS.map((f) => [f.value, f.label]));
const OPERATOR_LABELS   = Object.fromEntries(NUMERIC_OPERATORS.map((o) => [o.value, o.label]));
const DATE_OP_LABELS    = Object.fromEntries(DATE_OPERATORS.map((o) => [o.value, o.label]));
const FIELD_LABELS      = Object.fromEntries(FILTER_FIELDS.map((f) => [f.value, f.label]));
const FIELD_TYPES       = Object.fromEntries(FILTER_FIELDS.map((f) => [f.value, f.type]));

function chipLabel(f) {
  const label = FIELD_LABELS[f.field] || f.field;
  const type  = FIELD_TYPES[f.field];
  if (type === "boolean") return `${label}: ${f.value === "True" ? "Yes" : "No"}`;
  if (type === "date")    return `${label} ${DATE_OP_LABELS[f.operator] || f.operator} ${f.value}`;
  return `${label} ${OPERATOR_LABELS[f.operator]} ${f.value}`;
}

// ── Shared styles ────────────────────────────────────────────────────────────

const toggleSx = {
  "& .MuiToggleButton-root": {
    color: "rgba(255,255,255,0.45)",
    borderColor: "rgba(255,255,255,0.12)",
    fontSize: "0.78rem",
    padding: "4px 14px",
    textTransform: "none",
  },
  "& .MuiToggleButton-root:first-of-type": {
    borderTopLeftRadius: "999px",
    borderBottomLeftRadius: "999px",
  },
  "& .MuiToggleButton-root:last-of-type": {
    borderTopRightRadius: "999px",
    borderBottomRightRadius: "999px",
  },
  "& .MuiToggleButton-root.Mui-selected": {
    color: "#FDB940",
    bgcolor: "rgba(253,185,64,0.08)",
    borderColor: "rgba(253,185,64,0.4)",
  },
  "& .MuiToggleButton-root.Mui-selected:hover": {
    bgcolor: "rgba(253,185,64,0.12)",
  },
};

const popoverInputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#e0e6f0",
    bgcolor: "rgba(255,255,255,0.04)",
    fontSize: "0.85rem",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(253,185,64,0.5)" },
  },
  "& .MuiInputBase-input": { color: "#e0e6f0", fontSize: "0.85rem" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
};

const popoverSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#1c2330",
      border: "1px solid rgba(255,255,255,0.1)",
      "& .MuiMenuItem-root": {
        color: "#e0e6f0",
        fontSize: "0.85rem",
        "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
        "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)", color: "#FDB940" },
      },
    },
  },
};

const popoverLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.35)",
  mb: "6px",
  mt: "14px",
};

// ── Control / Switch pills ───────────────────────────────────────────────────

const controlColors = {
  LS:           { bg: "rgba(253,185,64,0.12)",  border: "rgba(253,185,64,0.35)",  color: "#FDB940",              dashed: false },
  PS:           { bg: "rgba(46,147,250,0.12)",  border: "rgba(46,147,250,0.35)",  color: "#2E93FA",              dashed: false },
  Nil:          { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.45)", dashed: false },
  Waiting:      { bg: "transparent",            border: "rgba(253,185,64,0.5)",   color: "#FDB940",              dashed: true  },
  Recovering:   { bg: "rgba(220,108,254,0.12)", border: "rgba(220,108,254,0.35)", color: "#DC6CFE",              dashed: false },
  // Keys must match device_device.control_status exactly; an unknown value falls back
  // to Nil's grey below. "MaxRecovering" was never one of Django's CONTROL_CHOICES.
  MaxRec:       { bg: "rgba(6,182,212,0.12)",   border: "rgba(6,182,212,0.35)",   color: "#06B6D4",              dashed: false },
  MaxNil:       { bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.35)",   color: "#EF4444",              dashed: false },
  ForcedOn:     { bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.35)",   color: "#22C55E",              dashed: false },
  ForcedOff:    { bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.35)",   color: "#FCA5A5",              dashed: false },
};

function SwitchPill({ value }) {
  const on = value === "On";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "999px", background: on ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.06)", border: `1px solid ${on ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.12)"}` }}>
      <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", background: on ? "#22C55E" : "rgba(255,255,255,0.3)", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: on ? "#22C55E" : "rgba(255,255,255,0.45)", lineHeight: 1 }}>{value}</Typography>
    </Box>
  );
}

function ForceTag({ value }) {
  if (!value || value === "Nil") return null;
  const isOn = value === "ForcedOn";
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
      px: "7px", py: "2px", borderRadius: "999px", whiteSpace: "nowrap",
      bgcolor: isOn ? "rgba(38,191,0,0.12)" : "rgba(239,68,68,0.12)",
      color: isOn ? "#86EFAC" : "#FCA5A5",
      border: `1px solid ${isOn ? "rgba(38,191,0,0.45)" : "rgba(239,68,68,0.45)"}`,
    }}>
      <Typography sx={{ fontSize: "0.72rem", lineHeight: 1 }}>⛭</Typography>
      <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit", lineHeight: 1 }}>
        {isOn ? "Force On" : "Force Off"}
      </Typography>
    </Box>
  );
}

function ControlPill({ value }) {
  const cfg = controlColors[value] || controlColors.Nil;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", px: "8px", py: "3px", borderRadius: "999px", background: cfg.bg, border: `1px ${cfg.dashed ? "dashed" : "solid"} ${cfg.border}` }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>{value}</Typography>
    </Box>
  );
}

// Holds the latest filter state so GoTo can stamp it into history at click time.
// navigate(-1) restores history state (including this snapshot) even when the URL
// hasn't been updated yet by the urlSync effect, which fixes filter loss on drill-back.
let _filterSnapshot = {
  connectionStatus: "Connected",
  switchStatus: "",
  controlStatus: [],
  forcedStatus: "",
  activeFilters: [],
  sortModel: [{ field: "serial_number", sort: "asc" }],
};

function GoTo({ obj }) {
  const navigate = useNavigate();
  const handleClick = (serial_number, id) => {
    // Stamp both returnRow AND the current filter state onto the dashboard history entry
    // before navigating away. navigate(-1) on the back button restores this entry
    // (URL + state), giving us reliable filter/page restoration regardless of whether
    // the urlSync effect had already committed filter params to the URL.
    window.history.replaceState(
      { ...window.history.state, usr: { returnRow: serial_number, filters: _filterSnapshot } },
      ""
    );
    navigate(`/operator/historical-data/${serial_number}/${id}/`);
  };
  const isClickable = obj.hwi !== "No geyser connected" && obj.hwi !== "No device connected";
  return (
    <Chip
      label={obj.serial_number}
      onClick={isClickable ? () => handleClick(obj.serial_number, obj.id) : undefined}
      className="serial-chip"
      sx={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)", fontFamily: "monospace", fontSize: "0.75rem", height: "26px", cursor: isClickable ? "pointer" : "default", "& .MuiChip-label": { px: "10px" } }}
    />
  );
}

const columns = [
  { field: "serial_number", headerName: "ID", flex: 1.1, minWidth: 140, sortable: false, renderCell: (params) => <GoTo obj={params.row} /> },
  {
    field: "hwi",
    headerName: "HWI",
    flex: 1,
    minWidth: 90,
    sortable: false,
    renderCell: (params) => {
      const v = params.value;
      const n = parseFloat(v);
      return isNaN(n) ? "" : parseFloat(n.toFixed(2)).toString();
    },
  },
  { field: "switch_status", headerName: "Switch", flex: 1.2, minWidth: 110, sortable: false, renderCell: (params) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      {params.row.switch_status && <SwitchPill value={params.row.switch_status} />}
      <ForceTag value={params.row.forced_status} />
    </Box>
  )},
  { field: "control_status", headerName: "Control", flex: 1, minWidth: 90, sortable: false, renderCell: (params) => params.row.control_status ? <ControlPill value={params.row.control_status} /> : null },
  {
    field: "power",
    headerName: "Power (kW)",
    flex: 0.9,
    minWidth: 85,
    sortable: false,
    renderHeader: () => <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 600, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Power <span style={{ textTransform: "none" }}>(kW)</span></span>,
    valueFormatter: (params) => {
      const n = parseFloat(params.value);
      return isNaN(n) ? "" : fixed(n);
    },
  },
  {
    field: "latest_power_rating",
    headerName: "Max Power (kW)",
    flex: 0.9,
    minWidth: 95,
    sortable: false,
    renderHeader: () => <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 600, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Max Power <span style={{ textTransform: "none" }}>(kW)</span></span>,
    valueFormatter: (params) => {
      const n = parseFloat(params.value);
      return isNaN(n) ? "" : fixed(n);
    },
  },
  { field: "last_switch_controlled", headerName: "Last Used Switch (SAST)", flex: 1.3, minWidth: 155, sortable: false, valueFormatter: (params) => formatSAST(params.value).replace(" SAST", "") },
  { field: "lower_limit_flagged", headerName: "Lower Limit (%)", flex: 0.7, minWidth: 110, sortable: false, renderCell: (params) => params.row.lower_limit_flagged ? <FlagIcon sx={{ color: "red" }} /> : null },
  {
    field: "is_baseline",
    headerName: "Baseline",
    flex: 0.7,
    minWidth: 80,
    sortable: false,
    renderHeader: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: "rgba(255,255,255,0.4)", fontWeight: 600, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        <span>{params.colDef.headerName}</span>
        <Tooltip
          title={
            <React.Fragment>
              <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", lineHeight: 1.3 }}>Baseline device</Typography>
              <Typography sx={{ fontSize: "0.78rem", mt: "4px", lineHeight: 1.4 }}>
                Devices flagged as baseline are excluded from DSM dispatch — they form the control group for M&V.
              </Typography>
            </React.Fragment>
          }
          placement="top"
          arrow
        >
          <InfoOutlinedIcon sx={{ fontSize: "13px", color: "rgba(255,255,255,0.35)", cursor: "help" }} />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => params.row.is_baseline ? <FlagIcon sx={{ color: "#03A60A" }} /> : null,
  },
];

// ── Main component ───────────────────────────────────────────────────────────

export default function OperatorDataGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // On drill-back, location.state has { returnRow, filters } stamped by GoTo before navigation.
  // Restore filter state from there rather than from URL params, because the URL may not have
  // been updated yet by the urlSync effect at the time the chip was clicked.
  const _rf = location.state?.returnRow ? (location.state?.filters ?? null) : null;

  // const [serialNumber, setSerialNumber]      = React.useState("");  // unused — check before removing
  const [controlStatus, setControlStatus]       = React.useState(
    () => _rf ? (_rf.controlStatus ?? []) : (searchParams.get("ctrl")?.split(",") ?? [])
  );
  const [connectionStatus, setConnectionStatus] = React.useState(
    () => _rf ? (_rf.connectionStatus ?? "Connected") : (searchParams.get("conn") ?? "Connected")
  );
  const [switchStatus, setSwitchStatus]         = React.useState(
    () => _rf ? (_rf.switchStatus ?? "") : (searchParams.get("sw") ?? "")
  );
  const [forcedStatus, setForcedStatus]         = React.useState(
    () => _rf ? (_rf.forcedStatus ?? "") : (searchParams.get("fs") ?? "")
  );
  // const [isBaseline, setIsBaseline]             = React.useState("");  // moved to chip filter model (NEU-607)
  const [filteredRows, setFilteredRows]         = React.useState([]);
  const [searchQuery, setSearchQuery]           = useState("");
  const [debouncedSearch, setDebouncedSearch]   = useState("");
  const [queryParams, setParamsQuery]           = useState(() => {
    if (_rf) {
      const { connectionStatus: rf_conn, switchStatus: rf_sw, controlStatus: rf_ctrl, forcedStatus: rf_fs, sortModel: rf_sort, activeFilters: rf_af } = _rf;
      const sf = rf_sort?.[0]?.field ?? "serial_number";
      const sd = rf_sort?.[0]?.sort ?? "asc";
      const params = { connectionStatus: rf_conn || "Connected", sortField: sf, sortOrder: sd };
      if (rf_sw) params.switchStatus = rf_sw;
      if (rf_fs) params.forcedStatus = rf_fs;
      if (rf_ctrl?.length > 0) params.controlStatus = rf_ctrl.join(",");
      rf_af?.forEach(({ field, operator, value }) => {
        if (field === "hwi")                      { params.hwiOperator = operator; params.hwiValue = value; }
        else if (field === "power")               { params.powerOperator = operator; params.powerValue = value; }
        else if (field === "latest_power_rating") { params.maxPowerOperator = operator; params.maxPowerValue = value; }
        else if (field === "lower_limit_flagged") { params.lowerLimitFlagged = value; }
        else if (field === "last_switch_controlled") { params.lastSwitchOperator = operator; params.lastSwitchValue = value; }
        else if (field === "is_baseline")         { params.isBaseline = value; }
      });
      return params;
    }
    const page    = Number(searchParams.get("page") ?? 0);
    const conn    = searchParams.get("conn");
    const sw      = searchParams.get("sw");
    const ctrl    = searchParams.get("ctrl");
    const sf      = searchParams.get("sort") ?? "serial_number";
    const sd      = searchParams.get("sortDir") ?? "asc";
    const params  = { connectionStatus: conn || "Connected", sortField: sf, sortOrder: sd };
    if (page > 0)  { params.page = page; params.pagesize = 20; }
    if (sw)          params.switchStatus   = sw;
    if (ctrl)        params.controlStatus  = ctrl;
    FILTER_FIELDS.forEach(({ value: field }) => {
      const raw = searchParams.get(`f_${field}`);
      if (!raw) return;
      const ci = raw.indexOf(":");
      if (ci === -1) return;
      const op = raw.slice(0, ci), val = raw.slice(ci + 1);
      if (field === "hwi")                  { params.hwiOperator        = op; params.hwiValue        = val; }
      else if (field === "power")           { params.powerOperator      = op; params.powerValue      = val; }
      else if (field === "latest_power_rating") { params.maxPowerOperator = op; params.maxPowerValue = val; }
      else if (field === "lower_limit_flagged") { params.lowerLimitFlagged = val; }
      else if (field === "last_switch_controlled") { params.lastSwitchOperator = op; params.lastSwitchValue = val; }
      else if (field === "is_baseline")     { params.isBaseline         = val; }
    });
    return params;
  });
  const [focusedSerial, setFocusedSerial]       = useState(location.state?.returnRow || null);

  // Advanced filters
  const [activeFilters, setActiveFilters] = useState(() => {
    if (_rf) return _rf.activeFilters ?? [];
    const filters = [];
    FILTER_FIELDS.forEach(({ value: field }) => {
      const raw = searchParams.get(`f_${field}`);
      if (!raw) return;
      const ci = raw.indexOf(":");
      if (ci === -1) return;
      filters.push({ field, operator: raw.slice(0, ci), value: raw.slice(ci + 1) });
    });
    return filters;
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftField, setDraftField]       = useState("hwi");
  const [draftOperator, setDraftOperator] = useState("gte");
  const [draftValue, setDraftValue]       = useState("");

  // Sort
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortModel, setSortModel]       = useState(() => {
    if (_rf) return _rf.sortModel ?? [{ field: "serial_number", sort: "asc" }];
    const sf = searchParams.get("sort") ?? "serial_number";
    const sd = searchParams.get("sortDir") ?? "asc";
    return [{ field: sf, sort: sd }];
  });

  const currentSort = sortModel[0] || { field: "serial_number", sort: "asc" };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleConnectionFilterChange = (event) => {
    const newConnectionStatus = connectionStatus === event.target.value ? "" : event.target.value;
    setConnectionStatus(newConnectionStatus);
    setParamsQuery((prev) => ({ ...prev, connectionStatus: newConnectionStatus }));
  };

  const handleControlFilterChange = (event, newControlStatus) => {
    setControlStatus(newControlStatus);
    setParamsQuery((prev) => ({ ...prev, controlStatus: newControlStatus.length > 0 ? newControlStatus.join(",") : "" }));
  };

  const handleSwitchStatusFilterChange = (event) => {
    const newSwitchStatus = switchStatus === event.target.value ? "" : event.target.value;
    setSwitchStatus(newSwitchStatus);
    setParamsQuery((prev) => ({ ...prev, switchStatus: newSwitchStatus }));
  };

  const handleForcedStatusFilterChange = (event) => {
    const newForcedStatus = forcedStatus === event.target.value ? "" : event.target.value;
    setForcedStatus(newForcedStatus);
    setParamsQuery((prev) => ({ ...prev, forcedStatus: newForcedStatus }));
  };

  // const handleisBaselineFilterChange = (event) => {  // removed — Baseline now in chip filter model (NEU-607)
  //   const newIsBaseline = isBaseline === event.target.value ? "" : event.target.value;
  //   setIsBaseline(newIsBaseline);
  //   setParamsQuery((prev) => ({ ...prev, isBaseline: newIsBaseline }));
  // };

  // Advanced filter handlers
  const handleFieldChange = (newField) => {
    const fieldDef = FILTER_FIELDS.find((f) => f.value === newField);
    setDraftField(newField);
    setDraftOperator("gte");
    setDraftValue(fieldDef?.type === "boolean" ? "True" : "");
  };

  const handleApplyFilter = () => {
    const fieldDef = FILTER_FIELDS.find((f) => f.value === draftField);
    if (fieldDef?.type !== "boolean" && !draftValue.trim()) return;
    const others = activeFilters.filter((f) => f.field !== draftField);
    setActiveFilters([...others, { field: draftField, operator: draftOperator, value: draftValue.trim() }]);
    setFilterAnchorEl(null);
    setDraftValue("");
    setFocusedSerial(null);
  };

  const handleRemoveFilter = (field) => {
    setActiveFilters((prev) => prev.filter((f) => f.field !== field));
    setFocusedSerial(null);
  };

  // Sort handlers
  const handleSortFieldSelect = (field) => {
    const newModel = [{ field, sort: "asc" }];
    setSortModel(newModel);
    setParamsQuery((prev) => ({ ...prev, sortField: field, sortOrder: "asc" }));
    setSortAnchorEl(null);
    setFocusedSerial(null);
  };

  const handleToggleSortDirection = (e) => {
    e.stopPropagation();
    const newSort = currentSort.sort === "asc" ? "desc" : "asc";
    const newModel = [{ field: currentSort.field, sort: newSort }];
    setSortModel(newModel);
    setParamsQuery((prev) => ({ ...prev, sortOrder: newSort }));
    setFocusedSerial(null);
  };

  // ── Effects ───────────────────────────────────────────────────────────────

  // Sync advanced filters → queryParams
  useEffect(() => {
    const get = (field) => activeFilters.find((f) => f.field === field);
    const hwi        = get("hwi");
    const pwr        = get("power");
    const maxPwr     = get("latest_power_rating");
    const ll         = get("lower_limit_flagged");
    const lastSwitch = get("last_switch_controlled");
    const baseline   = get("is_baseline");
    setParamsQuery((prev) => ({
      ...prev,
      hwiOperator:        hwi        ? hwi.operator        : "",
      hwiValue:           hwi        ? hwi.value           : "",
      powerOperator:      pwr        ? pwr.operator        : "",
      powerValue:         pwr        ? pwr.value           : "",
      maxPowerOperator:   maxPwr     ? maxPwr.operator     : "",
      maxPowerValue:      maxPwr     ? maxPwr.value        : "",
      lowerLimitFlagged:  ll         ? ll.value            : "",
      lastSwitchOperator: lastSwitch ? lastSwitch.operator : "",
      lastSwitchValue:    lastSwitch ? lastSwitch.value    : "",
      isBaseline:         baseline   ? baseline.value      : "",
    }));
  }, [activeFilters]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setParamsQuery((prev) => ({
      ...prev,
      searchQuery: debouncedSearch,
    }));
  }, [debouncedSearch]);

  // Keep the module-level snapshot current so GoTo can read it at click time.
  useEffect(() => {
    _filterSnapshot = { connectionStatus, switchStatus, controlStatus, forcedStatus, activeFilters, sortModel };
  }, [connectionStatus, switchStatus, controlStatus, forcedStatus, activeFilters, sortModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── URL state sync ────────────────────────────────────────────────────────
  // Persist filter/sort state to URL so refresh and browser-back restore them.
  const urlSyncMounted = useRef(false);
  useEffect(() => {
    if (!urlSyncMounted.current) { urlSyncMounted.current = true; return; }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (connectionStatus && connectionStatus !== "Connected") p.set("conn", connectionStatus);
      else p.delete("conn");
      if (switchStatus) p.set("sw", switchStatus); else p.delete("sw");
      if (forcedStatus) p.set("fs", forcedStatus); else p.delete("fs");
      if (controlStatus.length > 0) p.set("ctrl", controlStatus.join(",")); else p.delete("ctrl");
      const cs = sortModel[0] || { field: "serial_number", sort: "asc" };
      if (cs.field !== "serial_number") p.set("sort", cs.field); else p.delete("sort");
      if (cs.sort !== "asc") p.set("sortDir", cs.sort); else p.delete("sortDir");
      FILTER_FIELDS.forEach(({ value: field }) => {
        const f = activeFilters.find((af) => af.field === field);
        if (f) p.set(`f_${field}`, `${f.operator}:${f.value}`);
        else p.delete(`f_${field}`);
      });
      return p;
    });
  }, [connectionStatus, switchStatus, controlStatus, forcedStatus, sortModel, activeFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Find-page on drill-back ───────────────────────────────────────────────
  // When returning from a device detail page, ask the backend which page the
  // device is currently on (data may have shifted since we left).
  // Reads filter state from location.state.filters (stamped by GoTo at click time)
  // rather than from URL params, since the URL may be empty on drill-back.
  const findPageParams = useRef((() => {
    const row = location.state?.returnRow;
    if (!row) return null;
    const rf = location.state?.filters;
    if (rf) {
      const { connectionStatus, switchStatus, controlStatus, sortModel, activeFilters } = rf;
      const sf = sortModel?.[0]?.field ?? "serial_number";
      const sd = sortModel?.[0]?.sort ?? "asc";
      const p = { serial_number: row, pagesize: 20, connectionStatus: connectionStatus || "Connected", sortField: sf, sortOrder: sd };
      if (switchStatus) p.switchStatus = switchStatus;
      if (controlStatus?.length > 0) p.controlStatus = controlStatus.join(",");
      activeFilters?.forEach(({ field, operator, value }) => {
        if (field === "hwi")                      { p.hwiOperator = operator; p.hwiValue = value; }
        else if (field === "power")               { p.powerOperator = operator; p.powerValue = value; }
        else if (field === "latest_power_rating") { p.maxPowerOperator = operator; p.maxPowerValue = value; }
        else if (field === "lower_limit_flagged") { p.lowerLimitFlagged = value; }
        else if (field === "last_switch_controlled") { p.lastSwitchOperator = operator; p.lastSwitchValue = value; }
        else if (field === "is_baseline")         { p.isBaseline = value; }
      });
      return p;
    }
    // Fallback: no stored filter snapshot — try URL params
    const conn = searchParams.get("conn");
    const sw   = searchParams.get("sw");
    const ctrl = searchParams.get("ctrl");
    const sf   = searchParams.get("sort") ?? "serial_number";
    const sd   = searchParams.get("sortDir") ?? "asc";
    const p    = { serial_number: row, pagesize: 20, connectionStatus: conn || "Connected", sortField: sf, sortOrder: sd };
    if (sw)   p.switchStatus  = sw;
    if (ctrl) p.controlStatus = ctrl;
    FILTER_FIELDS.forEach(({ value: field }) => {
      const raw = searchParams.get(`f_${field}`);
      if (!raw) return;
      const ci = raw.indexOf(":");
      if (ci === -1) return;
      const op = raw.slice(0, ci), val = raw.slice(ci + 1);
      if (field === "hwi")                      { p.hwiOperator        = op; p.hwiValue        = val; }
      else if (field === "power")               { p.powerOperator      = op; p.powerValue      = val; }
      else if (field === "latest_power_rating") { p.maxPowerOperator   = op; p.maxPowerValue   = val; }
      else if (field === "lower_limit_flagged") { p.lowerLimitFlagged  = val; }
      else if (field === "last_switch_controlled") { p.lastSwitchOperator = op; p.lastSwitchValue = val; }
      else if (field === "is_baseline")         { p.isBaseline         = val; }
    });
    return p;
  })()).current;

  const { data: findPageData } = useGetDSMMeterFindPageQuery(findPageParams, {
    skip: !findPageParams,
  });

  useEffect(() => {
    if (findPageData?.page !== undefined) {
      const newPage = findPageData.page;
      setPaginationModel((prev) => ({ ...prev, page: newPage }));
      setParamsQuery((prev) => ({
        ...(typeof prev === "object" ? prev : {}),
        page: newPage,
        pagesize: 20,
      }));
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("page", String(newPage));
        return p;
      });
    }
  }, [findPageData]); // eslint-disable-line react-hooks/exhaustive-deps

  const [paginationModel, setPaginationModel] = useState({ page: Number(searchParams.get("page") ?? 0), pageSize: 20 });
  const [rowCount, setRowCount]               = useState(0);

  const {
    data: dsmMeters,
    isLoading: isDSMMetersLoading,
    isFetching: isDSMMetersFetching,
    isSuccess: isDSMMetersSuccess,
    isError: isDSMMetersError,
    refetch,
  } = useGetDSMMetersQuery(queryParams, { pollingInterval: 60000 });

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const filterParams = {};
      if (searchQuery) filterParams.searchQuery = searchQuery;
      if (connectionStatus) filterParams.connectionStatus = connectionStatus;
      if (switchStatus)     filterParams.switchStatus     = switchStatus;
      if (controlStatus.length > 0) filterParams.controlStatus = controlStatus.join(",");
      if (forcedStatus) filterParams.forcedStatus = forcedStatus;
      const getFilter = (field) => activeFilters.find((f) => f.field === field);
      const baseline = getFilter("is_baseline");
      if (baseline)         filterParams.isBaseline       = baseline.value;
      const hwi = getFilter("hwi");
      const pwr = getFilter("power");
      const maxPwr = getFilter("latest_power_rating");
      const ll = getFilter("lower_limit_flagged");
      const lastSwitch = getFilter("last_switch_controlled");
      if (hwi)       { filterParams.hwiOperator       = hwi.operator;       filterParams.hwiValue       = hwi.value; }
      if (pwr)       { filterParams.powerOperator      = pwr.operator;       filterParams.powerValue      = pwr.value; }
      if (maxPwr)    { filterParams.maxPowerOperator   = maxPwr.operator;    filterParams.maxPowerValue   = maxPwr.value; }
      if (ll)        { filterParams.lowerLimitFlagged  = ll.value; }
      if (lastSwitch){ filterParams.lastSwitchOperator = lastSwitch.operator; filterParams.lastSwitchValue = lastSwitch.value; }
      const path = concatPathWithParams("/dsm/meters/", { ...filterParams, page: 0, pagesize: 99999 });
      const response = await fetch(`${config.REACT_APP_SECRET_API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const result = await response.json();
      const rows = result?.data ?? [];
      downloadCSV(rows, DEVICE_CSV_HEADERS, `devices-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error("Device CSV download failed", err);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (isDSMMetersSuccess && dsmMeters) {
      setFilteredRows(dsmMeters.data);
      setRowCount(dsmMeters.total_devices ?? 0);
    }
  }, [isDSMMetersSuccess, dsmMeters]);

  // Scroll the focused row into view on drill-back.
  // Fires once when the correct page's rows land in filteredRows.
  // The ref guard prevents re-scrolling on subsequent 60 s poll re-fetches.
  const focusScrolledRef = useRef(false);
  useEffect(() => {
    if (!focusedSerial || focusScrolledRef.current) return;
    if (!filteredRows?.some((r) => r.serial_number === focusedSerial)) return;
    // DataGrid (autoHeight) does a two-pass internal render (measure → layout).
    // A single rAF fires between those passes before the row element is in the DOM.
    // 200 ms gives DataGrid time to finish both passes reliably.
    const timer = setTimeout(() => {
      const el = document.querySelector(".row-focused");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        focusScrolledRef.current = true;
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [filteredRows, focusedSerial]);

  // ── Loading / error states ────────────────────────────────────────────────

  if (rowCount === 0 && (isDSMMetersLoading || !isDSMMetersSuccess)) {
    return (
      <Grid item sx={{ width: "100%", display: "flex", alignItems: "center", gap: 2, p: 2 }}>
        <CircularProgress size={20} sx={{ color: "rgba(255,255,255,0.45)" }} />
        <Typography sx={{ color: "rgba(255,255,255,0.45)" }}>Loading devices…</Typography>
      </Grid>
    );
  }

  // if (isDSMMetersError) {  // removed early return — error now shown as inline banner (NEU-607)
  //   return (
  //     <Grid item sx={{ width: "100%", p: 2 }}>
  //       <Typography sx={{ color: "#e05050" }}>Error retrieving devices</Typography>
  //     </Grid>
  //   );
  // }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Grid item sx={{ width: "100%", mb: 5 }}>
      <Box sx={{ background: "#161B22", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", overflow: "hidden" }}>

        {/* ── Card header: title + SORT + download ── */}
        <Box sx={{ padding: "20px 20px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}>Devices</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>Server-paginated · 60 s polling</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {/* SORT button */}
            <Box
              onClick={(e) => setSortAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                px: "10px",
                py: "5px",
                borderRadius: "6px",
                border: "1px solid rgba(255,255,255,0.12)",
                cursor: "pointer",
                bgcolor: "rgba(255,255,255,0.03)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>
                Sort
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "rgba(255,255,255,0.65)", mx: "2px" }}>
                {SORT_FIELD_LABELS[currentSort.field] || "Serial"}
              </Typography>
              <Box
                onClick={handleToggleSortDirection}
                sx={{ display: "flex", alignItems: "center", color: "#FDB940", "&:hover": { opacity: 0.75 } }}
              >
                {currentSort.sort === "asc"
                  ? <ArrowUpwardIcon sx={{ fontSize: "0.9rem" }} />
                  : <ArrowDownwardIcon sx={{ fontSize: "0.9rem" }} />}
              </Box>
            </Box>

            {/* Download */}
            <IconButton
              onClick={handleDownload}
              disabled={isDownloading}
              size="small"
              title="Download all devices as CSV"
              sx={{ color: "rgba(255,255,255,0.45)", mt: "-2px", "&:hover": { color: "#FDB940", bgcolor: "rgba(253,185,64,0.08)" } }}
            >
              {isDownloading
                ? <CircularProgress size={16} sx={{ color: "#FDB940" }} />
                : <FileDownloadIcon sx={{ fontSize: "1.15rem" }} />}
            </IconButton>
          </Box>
        </Box>

        {/* ── Active filter chips + Add filter ── */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px", padding: "12px 20px 0" }}>
          {activeFilters.map((f) => (
            <Chip
              key={f.field}
              label={chipLabel(f)}
              onDelete={() => handleRemoveFilter(f.field)}
              size="small"
              sx={{
                bgcolor: "rgba(253,185,64,0.1)",
                border: "1px solid rgba(253,185,64,0.3)",
                color: "#FDB940",
                fontSize: "0.78rem",
                fontWeight: 600,
                "& .MuiChip-deleteIcon": { color: "rgba(253,185,64,0.6)", "&:hover": { color: "#FDB940" } },
              }}
            />
          ))}
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: "0.9rem" }} />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              color: "rgba(255,255,255,0.45)",
              fontSize: "0.78rem",
              fontWeight: 500,
              textTransform: "none",
              px: "10px",
              py: "4px",
              border: "1px dashed rgba(255,255,255,0.15)",
              borderRadius: "999px",
              "&:hover": { color: "#FDB940", borderColor: "rgba(253,185,64,0.4)", bgcolor: "rgba(253,185,64,0.05)" },
            }}
          >
            Add filter
          </Button>
        </Box>

        {/* ── Toggle filter bar + search ── */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", padding: "12px 20px 12px" }}>
          <ToggleButtonGroup sx={toggleSx} value={connectionStatus} exclusive onChange={handleConnectionFilterChange}>
            <ToggleButton value="Connected">Connected</ToggleButton>
            <ToggleButton value="Disconnected">Disconnected</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup sx={toggleSx} value={switchStatus} exclusive onChange={handleSwitchStatusFilterChange}>
            <ToggleButton value="On">On</ToggleButton>
            <ToggleButton value="Off">Off</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup sx={toggleSx} value={controlStatus} onChange={handleControlFilterChange}>
            <ToggleButton value="Nil">Nil</ToggleButton>
            <ToggleButton value="MaxNil">MaxNil</ToggleButton>
            <ToggleButton value="PS">PS</ToggleButton>
            <ToggleButton value="LS">LS</ToggleButton>
            <ToggleButton value="Waiting">Waiting</ToggleButton>
            <ToggleButton value="Recovering">Recovering</ToggleButton>
            <ToggleButton value="MaxRec">MaxRec</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup sx={toggleSx} value={forcedStatus} exclusive onChange={handleForcedStatusFilterChange}>
            <ToggleButton value="ForcedOn">Force On</ToggleButton>
            <ToggleButton value="ForcedOff">Force Off</ToggleButton>
          </ToggleButtonGroup>
          {/* <ToggleButtonGroup sx={toggleSx} value={isBaseline} exclusive onChange={handleisBaselineFilterChange}>
            <ToggleButton value="True">Baseline</ToggleButton>
          </ToggleButtonGroup> */}
          <TextField
            sx={{
              marginLeft: "auto",
              "& .MuiOutlinedInput-root": {
                color: "#e0e6f0",
                bgcolor: "rgba(255,255,255,0.04)",
                "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                "&.Mui-focused fieldset": { borderColor: "rgba(253,185,64,0.5)" },
              },
              "& .MuiInputBase-input": { color: "#e0e6f0", fontSize: "0.82rem" },
              "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.3)", opacity: 1 },
              "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.3)" },
            }}
            size="small"
            variant="outlined"
            value={searchQuery}
            placeholder="Search Serial Number"
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: "1rem" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ── Inline error banner ── */}
        {isDSMMetersError && filteredRows.length > 0 && (
          <Box sx={{ mx: "20px", mb: "12px", p: "12px 16px", borderRadius: "8px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#EF4444" }}>Couldn't apply that filter</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>
                We couldn't update the device list just now. Your previous results are still shown. Try again in a moment.
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={refetch}
              sx={{ color: "#EF4444", border: "1px solid rgba(239,68,68,0.4)", textTransform: "none", fontSize: "0.8rem", fontWeight: 600, borderRadius: "6px", px: "12px", flexShrink: 0, "&:hover": { bgcolor: "rgba(239,68,68,0.08)" } }}
            >
              Retry
            </Button>
          </Box>
        )}

        {/* ── DataGrid ── */}
        <DataGrid
          disableColumnMenu
          sx={{
            bgcolor: "transparent",
            color: "#e0e6f0",
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            "& .MuiDataGrid-columnHeaders": { bgcolor: "rgba(255,255,255,0.02)", borderBottom: "1px solid rgba(255,255,255,0.08)" },
            "& .MuiDataGrid-columnHeaderTitle": { color: "rgba(255,255,255,0.4)", fontWeight: 600, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" },
            "& .MuiDataGrid-cell": { borderColor: "rgba(255,255,255,0.05)", color: "#e0e6f0" },
            "& .MuiDataGrid-row:hover": { bgcolor: "rgba(253,185,64,0.05)" },
            "& .MuiDataGrid-row:hover .serial-chip": { borderColor: "rgba(253,185,64,0.55)", color: "#FDB940" },
            "& .row-focused": { background: "rgba(253,185,64,0.08) !important", boxShadow: "inset 4px 0 0 0 #FDB940", animation: "rowFocusPulse 1.4s ease-out 1" },
            "@keyframes rowFocusPulse": {
              "0%":   { boxShadow: "inset 4px 0 0 0 #FDB940, 0 0 0 0 rgba(253,185,64,0.3)" },
              "50%":  { boxShadow: "inset 4px 0 0 0 #FDB940, 0 0 0 8px rgba(253,185,64,0)" },
              "100%": { boxShadow: "inset 4px 0 0 0 #FDB940" },
            },
            "& .MuiDataGrid-footerContainer": { bgcolor: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", minHeight: "unset" },
            "& .MuiDataGrid-sortIcon": { color: "rgba(255,255,255,0.45)" },
            "& .MuiDataGrid-menuIconButton": { color: "rgba(255,255,255,0.45)" },
            "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.08)" },
            "& .MuiDataGrid-overlay": { bgcolor: "#161B22" },
          }}
          slots={{ pagination: CustomPagination }}
          getRowClassName={(params) => focusedSerial && params.row.serial_number === focusedSerial ? "row-focused" : ""}
          rows={filteredRows}
          columns={columns}
          autoHeight
          pageSizeOptions={[20]}
          disableSelectionOnClick
          disableRowSelectionOnClick={true}
          columnBuffer={3}
          paginationMode="server"
          sortingMode="server"
          rowCount={rowCount}
          loading={isDSMMetersFetching}
          paginationModel={paginationModel}
          sortModel={sortModel}
          onPaginationModelChange={(newModel) => {
            setPaginationModel(newModel);
            setParamsQuery((prev) => ({ ...prev, page: newModel.page, pagesize: newModel.pageSize }));
            setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("page", String(newModel.page)); p.delete("row"); return p; });
            setFocusedSerial(null);
          }}
          onSortModelChange={(newSortModel) => {
            setSortModel(newSortModel);
            if (newSortModel.length > 0) {
              const { field, sort } = newSortModel[0];
              setParamsQuery((prev) => ({ ...prev, sortField: field, sortOrder: sort }));
            } else {
              setParamsQuery((prev) => ({ ...prev, sortField: null, sortOrder: null }));
            }
          }}
        />
      </Box>

      {/* ── Add filter popover ── */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            bgcolor: "#161B22",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            p: "18px",
            width: 320,
            mt: "6px",
          },
        }}
      >
        {/* The design's close button: the popover could only be dismissed by clicking away (NEU-1056). */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4px" }}>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#fff" }}>
            Add filter
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={() => setFilterAnchorEl(null)}
            sx={{
              width: 22, height: 22, borderRadius: "6px", color: "rgba(255,255,255,0.45)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "#fff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", mb: "4px" }}>
          Filters combine with AND. Multiple values within a field combine with OR.
        </Typography>

        <Typography sx={popoverLabelSx}>Field</Typography>
        <Select
          fullWidth
          size="small"
          value={draftField}
          onChange={(e) => handleFieldChange(e.target.value)}
          sx={popoverInputSx}
          MenuProps={popoverSelectMenuProps}
          inputProps={{ sx: { color: "#e0e6f0", fontSize: "0.85rem" } }}
        >
          {FILTER_FIELDS.map((f) => (
            <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
          ))}
        </Select>

        {/* Operator — hidden for boolean fields */}
        {FIELD_TYPES[draftField] !== "boolean" && (
          <>
            <Typography sx={popoverLabelSx}>Operator</Typography>
            <Select
              fullWidth
              size="small"
              value={draftOperator}
              onChange={(e) => setDraftOperator(e.target.value)}
              MenuProps={popoverSelectMenuProps}
              inputProps={{ sx: { color: "#e0e6f0", fontSize: "0.85rem" } }}
            >
              {(FIELD_TYPES[draftField] === "date" ? DATE_OPERATORS : NUMERIC_OPERATORS).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </>
        )}

        {/* Value — adapts to field type */}
        <Typography sx={popoverLabelSx}>Value</Typography>
        {FIELD_TYPES[draftField] === "boolean" ? (
          <Select
            fullWidth
            size="small"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            MenuProps={popoverSelectMenuProps}
            inputProps={{ sx: { color: "#e0e6f0", fontSize: "0.85rem" } }}
          >
            <MenuItem value="True">Yes</MenuItem>
            <MenuItem value="False">No</MenuItem>
          </Select>
        ) : FIELD_TYPES[draftField] === "date" ? (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleApplyFilter(); }}
            sx={popoverInputSx}
            InputLabelProps={{ shrink: true }}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="e.g. 0.50"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleApplyFilter(); }}
            sx={popoverInputSx}
          />
        )}

        <Box sx={{ display: "flex", gap: "8px", mt: "18px", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => setFilterAnchorEl(null)}
            sx={{ color: "rgba(255,255,255,0.45)", textTransform: "none", fontSize: "0.82rem" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApplyFilter}
            disabled={FIELD_TYPES[draftField] !== "boolean" && !draftValue.trim()}
            sx={{
              bgcolor: "#FDB940",
              color: "#000",
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#e5a830" },
              "&:disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
            }}
          >
            Apply filter
          </Button>
        </Box>
      </Popover>

      {/* ── Sort field popover ── */}
      <Popover
        open={Boolean(sortAnchorEl)}
        anchorEl={sortAnchorEl}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            bgcolor: "#161B22",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            py: "6px",
            minWidth: 160,
            mt: "6px",
          },
        }}
      >
        {SORT_FIELDS.map((f) => (
          <Box
            key={f.value}
            onClick={() => handleSortFieldSelect(f.value)}
            sx={{
              px: "14px",
              py: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              bgcolor: currentSort.field === f.value ? "rgba(253,185,64,0.08)" : "transparent",
              "&:hover": { bgcolor: currentSort.field === f.value ? "rgba(253,185,64,0.12)" : "rgba(255,255,255,0.04)" },
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", color: currentSort.field === f.value ? "#FDB940" : "#e0e6f0", fontWeight: currentSort.field === f.value ? 600 : 400 }}>
              {f.label}
            </Typography>
            {currentSort.field === f.value && (
              currentSort.sort === "asc"
                ? <ArrowUpwardIcon sx={{ fontSize: "0.9rem", color: "#FDB940" }} />
                : <ArrowDownwardIcon sx={{ fontSize: "0.9rem", color: "#FDB940" }} />
            )}
          </Box>
        ))}
      </Popover>
    </Grid>
  );
}
